// Column-configuration helpers for the Projects list "Customize Table" menu.
// Column widths are Bootstrap grid units, so the selected set is capped at 12.

export const MAX_COLUMN_WIDTH = 12;

interface ConfigColumn {
  label: string;
  width: number;
  selected: boolean;
  position: number;
}

export const selectedColumnWidth = <T extends ConfigColumn>(
  columns: T[]
): number =>
  columns
    .filter((column) => column.selected)
    .reduce((sum, column) => sum + column.width, 0);

// The incoming column's own width counts: at 11/12 a width-2 column must be
// refused, not allowed through to 13/12.
export const canSelectColumn = <T extends ConfigColumn>(
  columns: T[],
  column: T
): boolean => selectedColumnWidth(columns) + column.width <= MAX_COLUMN_WIDTH;

// Moves the selected column at `from` to `to` (indices into the selected
// columns ordered by position) and renumbers selected positions from 1.
// Unselected columns are carried through untouched: the user view is saved
// whole, and scopestack-api's UserView#ensure_columns re-adds any missing
// column with its YAML default, which for most core columns is selected.
export const reorderSelectedColumns = <T extends ConfigColumn>(
  columns: T[],
  from: number,
  to: number
): T[] => {
  const selected = columns
    .filter((column) => column.selected)
    .sort((a, b) => a.position - b.position);
  if (from === to || !selected[from] || !selected[to]) return columns;

  const [moved] = selected.splice(from, 1);
  selected.splice(to, 0, moved);

  return [
    ...selected.map((column, index) => ({ ...column, position: index + 1 })),
    ...columns.filter((column) => !column.selected),
  ];
};
